<template>
  <v-container class="pt-3 pa-0">
    <v-row>
      <v-col cols="3" class="pr-0 pl-1">
        <v-tabs v-model="activeTab" direction="vertical" color="primary">
          <v-tab
            v-for="(file, key) in uploadedFiles"
            :key="key"
            :value="key"
            class="text-bold prepare-tab"
          >
            <v-icon
              v-if="file.status === 'success'"
              icon="mdi-check-circle-outline"
              color="rgb(23, 188, 22)"
              class="mr-2"
            />
            <v-icon
              v-else-if="file.status === 'error'"
              icon="mdi-alert-circle-outline"
              color="error"
              class="mr-2"
            />
            <v-icon v-else icon="mdi-circle-outline" color="grey-lighten-1" class="mr-2" />
            <span class="text-title-medium font-weight-bold text-break">{{ file.title }}</span>
          </v-tab>
          <v-tab v-if="xmlUploadFiles.length > 0" value="samlXml" class="prepare-tab">
            <v-icon
              v-if="!xmlUploadIncomplete"
              icon="mdi-check-circle-outline"
              color="rgb(23, 188, 22)"
              class="mr-2"
            />
            <v-icon v-else icon="mdi-circle-outline" color="grey-lighten-1" class="mr-2" />
            <span class="text-title-medium font-weight-bold text-break">SAML Metadata XML</span>
          </v-tab>
        </v-tabs>
      </v-col>
      <v-col cols="9" class="hub-prepare-content">
        <v-tabs-window v-model="activeTab">
          <v-tabs-window-item v-for="(file, key) in uploadedFiles" :key="key" :value="key">
            <div class="mb-2">
              <div class="prepare-title">{{ file.title }}</div>
              <file-upload
                :id="key"
                :title="`Upload CSV`"
                :file-extensions="csvFileExtensions"
                :loading="file.loading"
                @file-changed="onFileChanged"
              >
                <template #custom-info>
                  <div class="status-container">
                    <v-icon
                      v-if="file.status === 'success'"
                      icon="mdi-check-circle-outline"
                      color="rgb(23, 188, 22)"
                    />
                    <v-icon
                      v-else-if="file.status === 'error'"
                      icon="mdi-alert-circle-outline"
                      color="error"
                      @click="showErrorDialog(key)"
                    />
                  </div>
                </template>
              </file-upload>
            </div>
            <v-divider class="my-4" />
            <client-hub-tenant-management
              v-if="key === 'tenantCsvFile'"
              :ref="
                (el) => {
                  clientHubTenantManagement = el;
                }
              "
              :client-sid="clientSid"
              :upload-file-in-progress="uploadedFiles[key].loading"
              @save-success="fetchUploadedFiles"
            />
            <client-hub-oidc-idp-management
              v-if="key === 'externalIdpOidcCsvFile'"
              :ref="
                (el) => {
                  clientHubOidcIdpManagement = el;
                }
              "
              :client-sid="clientSid"
              @save-success="fetchUploadedFiles"
            />
            <client-hub-external-idp-saml-config
              v-if="key === 'externalIdpSamlCsvFile'"
              :ref="
                (el) => {
                  clientHubExternalIdpSamlConfig = el;
                }
              "
              :client-sid="clientSid"
              :upload-file-in-progress="uploadedFiles[key].loading"
              :has-uploaded-xml-file="hasUploadedXmlFile"
              @save-success="fetchUploadedFiles"
            />
            <client-hub-product-instance-management
              v-if="key === 'productInstanceCsvFile'"
              :ref="
                (el) => {
                  clientHubProductInstanceManagement = el;
                }
              "
              :client-sid="clientSid"
              @save-success="fetchUploadedFiles"
            />
            <client-hub-user-data-management
              v-if="key === 'userInfoCsvFile'"
              :ref="
                (el) => {
                  clientHubUserDataManagement = el;
                }
              "
              :upload-file-in-progress="uploadedFiles[key].loading"
              :client-sid="clientSid"
              @save-success="fetchUploadedFiles"
            />
          </v-tabs-window-item>

          <v-tabs-window-item value="samlXml">
            <div class="mb-2">
              <div class="prepare-title">SAML Metadata XML Collection</div>
            </div>
            <div class="d-flex align-center text-orange-accent-4 mb-4">
              <v-icon icon="mdi-alert" color="orange-accent-4" class="mr-2" />
              <span>
                Note: Uploaded XML file names will be converted to lowercase (e.g. MyFile.xml ->
                myfile.xml)
              </span>
            </div>
            <div
              v-for="xmlFile in xmlUploadFiles"
              :key="xmlFile.fileName"
              class="d-flex align-center xml-checklist-item"
            >
              <v-icon
                v-if="xmlFile.status === 'success'"
                icon="mdi-check-circle-outline"
                color="rgb(23, 188, 22)"
                class="mr-2"
              />
              <v-icon v-else icon="mdi-circle-outline" color="grey-lighten-1" class="mr-2" />
              <span :class="{ 'xml-filename-uploaded': xmlFile.status === 'success' }">
                {{ xmlFile.fileName }}
              </span>
            </div>
            <div class="mt-4">
              <file-upload
                id="xmlUpload"
                title="Upload Metadata XML"
                :file-extensions="xmlFileExtensions"
                :loading="xmlUploadLoading"
                @file-changed="onXmlFileChanged"
              >
                <template #custom-info>
                  <div class="status-container">
                    <span v-if="xmlUploadError" class="text-red-accent-4 d-flex align-center">
                      <v-icon color="error" class="mr-1"> mdi-alert-circle-outline </v-icon>
                      {{ xmlUploadError }}
                    </span>
                  </div>
                </template>
              </file-upload>
            </div>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-col>
    </v-row>

    <cs-form-dialog
      id="dlg-client-hub-migration-error"
      v-model="errorDialogDisplayed"
      body-id="dlg-client-hub-migration-error-body"
      heading="Hub Migration Error"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="errorDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <v-card
          v-for="error in errorFile.errors"
          :key="error"
          class="error-card"
          variant="outlined"
        >
          <v-card-text>
            <div class="error-message">{{ error.error }}</div>
            <div class="error-description">{{ error.description }}</div>
          </v-card-text>
        </v-card>
      </template>
    </cs-form-dialog>
  </v-container>
</template>

<script>
import FileUpload from './FileUpload';
import * as types from '../store/mutationTypes';
import ClientHubTenantManagement from './ClientHubTenantManagement';
import ClientHubUserDataManagement from './ClientHubUserDataManagement';
import ClientHubOidcIdpManagement from './ClientHubOidcIdpManagement';
import ClientHubExternalIdpSamlConfig from './ClientHubExternalIdpSamlConfig';
import ClientHubProductInstanceManagement from './ClientHubProductInstanceManagement';

export default {
  name: 'ClientHubPrepare',
  components: {
    FileUpload,
    ClientHubTenantManagement,
    ClientHubUserDataManagement,
    ClientHubOidcIdpManagement,
    ClientHubExternalIdpSamlConfig,
    ClientHubProductInstanceManagement
  },
  props: {
    clientSid: { type: String, required: false, default: '' }
  },

  data() {
    return {
      activeTab: 'tenantCsvFile',
      csvFileExtensions: '.csv',
      uploadedFiles: {},
      xmlFileExtensions: '.xml',
      xmlUploadFiles: [],
      xmlUploadLoading: false,
      xmlUploadError: null,
      fileNameToKeyMap: {
        migration_tenants: 'tenantCsvFile',
        migration_external_idp_oidc: 'externalIdpOidcCsvFile',
        migration_external_idp_saml: 'externalIdpSamlCsvFile',
        migration_product_instances: 'productInstanceCsvFile',
        migration_users: 'userInfoCsvFile'
      },
      errorDialogDisplayed: false,
      errorFile: {},
      clientHubTenantManagement: null,
      clientHubUserDataManagement: null,
      clientHubOidcIdpManagement: null,
      clientHubProductInstanceManagement: null,
      clientHubExternalIdpSamlConfig: null,
      fetchUploadedFilesInProgress: false
    };
  },
  computed: {
    xmlUploadIncomplete() {
      return (
        this.xmlUploadFiles.length > 0 &&
        this.xmlUploadFiles.some((file) => file.status !== 'success')
      );
    },
    csvUploadInProgress() {
      return Object.values(this.uploadedFiles).some((file) => file.loading);
    },
    hasUploadedXmlFile() {
      return this.xmlUploadFiles.some((file) => file.status === 'success');
    }
  },
  created() {
    this.uploadedFiles = this.createDefaultUploadFiles();
  },
  async mounted() {
    await this.fetchUploadedFiles();
  },
  methods: {
    async onFileChanged(id, fileInfo) {
      const type = this.uploadedFiles[id].type;
      this.uploadedFiles[id].loading = true;
      try {
        const patchRes = await this.$store.dispatch('hub-migration/postCsvUpload', {
          clientSid: this.clientSid,
          type,
          attachment: fileInfo.file
        });
        if (patchRes.error) {
          this.handleUploadFileError(id, patchRes.error);
        } else {
          await Promise.all([this.refreshComponentContent(id), this.fetchUploadedFiles()]);
        }
        this.uploadedFiles[id].loading = false;
      } catch (error) {
        this.uploadedFiles[id].loading = false;
      }
    },
    async refreshComponentContent(id) {
      if (id === 'tenantCsvFile') {
        await this.clientHubTenantManagement?.fetchHubCsvTenants();
      } else if (id === 'userInfoCsvFile') {
        await this.clientHubUserDataManagement?.fetchHubCsvUsers();
      } else if (id === 'externalIdpOidcCsvFile') {
        await this.clientHubOidcIdpManagement?.fetchOidcIdpInfo();
      } else if (id === 'productInstanceCsvFile') {
        await this.clientHubProductInstanceManagement?.fetchProductInstanceInfo();
      } else if (id === 'externalIdpSamlCsvFile') {
        await this.clientHubExternalIdpSamlConfig?.fetchHubCsvExternalIdpSaml();
      }
    },
    handleUploadFileError(id, error) {
      this.uploadedFiles[id].status = 'error';
      if (error.statusCode === 422) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.uploadedFiles[id].errors = error.contentErrors;
      } else {
        this.uploadedFiles[id].status = 'error';
        this.uploadedFiles[id].errors = [{ error: error.error || error.message, description: '' }];
      }
    },
    showErrorDialog(id) {
      this.errorDialogDisplayed = true;
      this.errorFile = this.uploadedFiles[id];
    },
    async fetchUploadedFiles() {
      try {
        if (this.fetchUploadedFilesInProgress) return;
        this.fetchUploadedFilesInProgress = true;

        const response = await this.$store.dispatch('hub-migration/getCsvList', {
          clientSid: this.clientSid
        });
        if (!response) {
          this.fetchUploadedFilesInProgress = false;
          return;
        }
        const { csv: uploadedCsvFiles = [], saml_xml: uploadedXmlFiles } = response;

        const refreshedUploadedFiles = this.createDefaultUploadFiles();

        Object.keys(refreshedUploadedFiles).forEach((key) => {
          refreshedUploadedFiles[key].loading = this.uploadedFiles[key]?.loading || false;
        });

        uploadedCsvFiles.forEach((file) => {
          const baseName = file.fileName.replace(/\.csv$/, '');
          const key = this.fileNameToKeyMap[baseName];
          if (key) refreshedUploadedFiles[key].status = 'success';
        });

        const uploadedXmlNames = (uploadedXmlFiles?.uploaded || []).map((file) => file.fileName);
        const refreshedXmlUploadFiles = (uploadedXmlFiles?.expected || []).map((fileName) => ({
          fileName,
          title: fileName,
          status: uploadedXmlNames.includes(fileName) ? 'success' : ''
        }));

        this.uploadedFiles = refreshedUploadedFiles;
        this.xmlUploadFiles = refreshedXmlUploadFiles;

        this.fetchUploadedFilesInProgress = false;
      } catch (error) {
        this.fetchUploadedFilesInProgress = false;
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      }
    },
    async onXmlFileChanged(_id, fileInfo) {
      this.xmlUploadLoading = true;
      this.xmlUploadError = null;
      try {
        const response = await this.$store.dispatch('hub-migration/postCsvUpload', {
          clientSid: this.clientSid,
          type: 'saml_xml',
          attachment: fileInfo.file
        });
        if (response.error) {
          this.handleXmlUploadError(response.error);
        } else {
          await this.fetchUploadedFiles();
        }
        this.xmlUploadLoading = false;
      } catch (error) {
        this.xmlUploadLoading = false;
      }
    },
    handleXmlUploadError(error) {
      if (error.statusCode === 422) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        const errorMessage = error.contentErrors.map((e) => e.error).join(', ');
        this.xmlUploadError = errorMessage;
      } else {
        this.xmlUploadError = error.error;
      }
    },
    createDefaultUploadFiles() {
      return {
        tenantCsvFile: { type: 'tenants', title: 'Tenant Information', status: '', loading: false },
        externalIdpOidcCsvFile: {
          type: 'external_idp_oidc',
          title: 'External IDP (OIDC) Information',
          status: '',
          loading: false
        },
        externalIdpSamlCsvFile: {
          type: 'external_idp_saml',
          title: 'External IDP (SAML) Information',
          status: '',
          loading: false
        },
        productInstanceCsvFile: {
          type: 'product_instances',
          title: 'Product Instance Information',
          status: '',
          loading: false
        },
        userInfoCsvFile: { type: 'users', title: 'User Information', status: '', loading: false }
      };
    },
    resetUploadedFiles() {
      this.uploadedFiles = this.createDefaultUploadFiles();
      this.errorFile = {};
      this.xmlUploadFiles = [];
      this.xmlUploadLoading = false;
      this.xmlUploadError = null;
    }
  }
};
</script>

<style scoped>
.prepare-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
}
.status-container {
  padding-top: 5px;
}
.error-card {
  margin-bottom: 10px;
}
.error-message {
  font-size: 12px;
  color: black;
}
.error-description {
  font-size: 12px;
  color: rgb(79, 78, 78);
}
.xml-checklist-item {
  padding: 4px 0;
  font-size: 14px;
}
.xml-filename-uploaded {
  color: rgb(79, 78, 78);
}
.prepare-tab {
  justify-content: flex-start !important;
  text-transform: none !important;
  white-space: normal !important;
  text-align: left !important;
}
:deep(.prepare-tab .v-btn__content) {
  font-size: 14px !important;
  white-space: normal !important;
  text-align: left !important;
}

.hub-prepare-content {
  padding-left: 50px !important;
  border-left: 1px solid #e0e0e0 !important;
}
</style>
